import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import AppIcon from '../../../../components/AppIcon';

interface Employee {
  id: string;
  name: string;
  overallScore: number;
  goalScore: number;
  collaborationScore: number;
  productivityScore: number;
  communicationScore: number;
  innovationScore: number;
  leadershipScore: number;
  goalsCompleted: number;
  totalGoals: number;
  checkIns: number;
  recognitions: number;
  reflections: number;
  strengths: string[];
  growthAreas: string[];
}

interface PerformanceInsightsPanelProps {
  employee: Employee | null;
}

const PerformanceInsightsPanel: React.FC<PerformanceInsightsPanelProps> = ({ employee }) => {
  const radarData = [
    { subject: 'Goal Achievement', A: employee?.goalScore || 82 },
    { subject: 'Collaboration', A: employee?.collaborationScore || 75 },
    { subject: 'Productivity', A: employee?.productivityScore || 88 },
    { subject: 'Communication', A: employee?.communicationScore || 70 },
    { subject: 'Innovation', A: employee?.innovationScore || 65 },
    { subject: 'Leadership', A: employee?.leadershipScore || 72 },
  ];

  const trendData = [
    { period: 'Q1', score: 71 },
    { period: 'Q2', score: 76 },
    { period: 'Q3', score: 80 },
    { period: 'Q4', score: employee?.overallScore || 82 },
  ];

  const metrics = [
    { label: 'Goals Completed', value: `${employee?.goalsCompleted || 8}/${employee?.totalGoals || 10}`, icon: 'Target' as const, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Check-ins Done', value: `${employee?.checkIns || 11}/12`, icon: 'MessageCircle' as const, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Recognitions', value: employee?.recognitions || 14, icon: 'Heart' as const, color: 'text-rose-600', bg: 'bg-rose-50' },
    { label: 'Reflections', value: `${employee?.reflections || 9}/12`, icon: 'BookOpen' as const, color: 'text-violet-600', bg: 'bg-violet-50' },
  ];

  const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd'];

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-indigo-600 rounded-lg flex items-center justify-center">
            <AppIcon name="Activity" size={18} color="white" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-base">Performance Snapshot Insights</h3>
            <p className="text-xs text-muted-foreground">Aggregated from review data & check-ins</p>
          </div>
        </div>
        <span className="text-xs bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full font-medium">
          Overall: {employee?.overallScore || 82}/100
        </span>
      </div>
      {/* Quick Metrics */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        {metrics?.map((m) => (
          <div key={m?.label} className={`${m?.bg} rounded-lg p-3`}>
            <div className={`${m?.color} mb-1`}>
              <AppIcon name={m?.icon} size={16} />
            </div>
            <div className={`text-lg font-bold ${m?.color}`}>{m?.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{m?.label}</div>
          </div>
        ))}
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Radar Chart */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Competency Radar</p>
          <ResponsiveContainer width="100%" height={180}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#64748b' }} />
              <Radar name="Score" dataKey="A" stroke="#6366f1" fill="#6366f1" fillOpacity={0.25} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Trend Bar Chart */}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-2">Quarterly Trend</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={trendData} barSize={28}>
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <YAxis domain={[60, 100]} tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                formatter={(v: number) => [`${v}/100`, 'Score']}
              />
              <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                {trendData?.map((_, i) => (
                  <Cell key={i} fill={COLORS?.[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Strengths & Areas */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="bg-emerald-50 rounded-lg p-3">
          <p className="text-xs font-semibold text-emerald-700 mb-2 flex items-center gap-1.5">
            <AppIcon name="TrendingUp" size={13} /> Top Strengths
          </p>
          <div className="space-y-1">
            {(employee?.strengths || ['Problem Solving', 'Technical Depth', 'Delivery Speed'])?.map((s) => (
              <div key={s} className="text-xs text-emerald-800 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0" />
                {s}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-amber-50 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
            <AppIcon name="Lightbulb" size={13} /> Growth Areas
          </p>
          <div className="space-y-1">
            {(employee?.growthAreas || ['Strategic Thinking', 'Cross-team Influence', 'Mentoring'])?.map((g) => (
              <div key={g} className="text-xs text-amber-800 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0" />
                {g}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceInsightsPanel;
